/**
 * Standard Inputs Component
 * Financial inputs for CCA and Manual valuation methods
 * Includes Revenue and EBITDA with dollar/percentage toggle
 */
import React from 'react';
import PropTypes from 'prop-types';
import { HelpCircle } from 'lucide-react';

const StandardInputs = ({
  formData,
  onInputChange,
  onShowHelp,
  setFormData,
  formatNumberWithCommas
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Annual Revenue */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Annual Revenue *
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onShowHelp('revenue');
            }}
            className="ml-1 text-blue-600 hover:text-blue-700"
          >
            <HelpCircle size={14} className="inline" />
          </button>
        </label>
        <div className="relative">
          <span className="absolute left-3 top-3 text-gray-500">$</span>
          <input
            type="text"
            name="revenue"
            value={formatNumberWithCommas(formData.revenue)}
            onChange={onInputChange}
            required
            placeholder="5,000,000"
            className="w-full pl-8 pr-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* EBITDA with dollar/percentage toggle */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-700">
            EBITDA *
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onShowHelp('ebitda');
              }}
              className="ml-1 text-blue-600 hover:text-blue-700"
            >
              <HelpCircle size={14} className="inline" />
            </button>
          </label>
          {/* EBITDA Input Type Toggle - Inline */}
          <div className="flex items-center space-x-2">
            <label className="flex items-center space-x-1 cursor-pointer">
              <input
                type="radio"
                name="ebitda_input_type"
                value="dollar"
                checked={formData.ebitda_input_type === 'dollar'}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    ebitda_input_type: 'dollar',
                    ebitda_margin: ''
                  }));
                }}
                className="text-blue-600"
              />
              <span className="text-xs">$</span>
            </label>
            <label className="flex items-center space-x-1 cursor-pointer">
              <input
                type="radio"
                name="ebitda_input_type"
                value="percentage"
                checked={formData.ebitda_input_type === 'percentage'}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    ebitda_input_type: 'percentage',
                    ebitda: '' // Clear dollar amount when switching to percentage
                  }));
                }}
                className="text-blue-600"
              />
              <span className="text-xs">%</span>
            </label>
          </div>
        </div>

        {formData.ebitda_input_type === 'dollar' ? (
          <>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-500">$</span>
              <input
                type="text"
                name="ebitda"
                value={formatNumberWithCommas(formData.ebitda)}
                onChange={onInputChange}
                required
                placeholder="1,000,000"
                className="w-full pl-8 pr-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            {formData.ebitda && formData.revenue && (
              <p className="text-xs text-gray-600 mt-1">
                EBITDA Margin: {((parseFloat(formData.ebitda) / parseFloat(formData.revenue)) * 100).toFixed(1)}%
              </p>
            )}
          </>
        ) : (
          <>
            <div className="relative">
              <input
                type="number"
                name="ebitda_margin"
                value={formData.ebitda_margin}
                onChange={(e) => {
                  const margin = parseFloat(e.target.value) || 0;
                  const revenue = parseFloat(formData.revenue) || 0;
                  const calculatedEbitda = revenue > 0 && margin > 0 ? (revenue * margin / 100) : '';

                  setFormData(prev => ({
                    ...prev,
                    ebitda_margin: e.target.value,
                    ebitda: calculatedEbitda
                  }));
                }}
                required
                placeholder="15"
                min="0"
                max="100"
                step="0.1"
                className="w-full pr-8 pl-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="absolute right-3 top-3 text-gray-500">%</span>
            </div>
            {formData.ebitda_margin && formData.revenue && (
              <p className="text-xs text-gray-600 mt-1">
                EBITDA Amount: ${parseFloat(formData.ebitda || 0).toLocaleString()}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

StandardInputs.propTypes = {
  formData: PropTypes.object.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onShowHelp: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
  formatNumberWithCommas: PropTypes.func.isRequired
};

export default StandardInputs;
